import { useState, useMemo } from "react";
import AppShell from "@/components/layout/AppShell";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import api from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ClockClockwise, MagnifyingGlass } from "@phosphor-icons/react";
import TimelineView, { MiniStat, fmtNum } from "@/components/common/TimelineView";

export default function DeliveryTimelinePage() {
  const [q, setQ] = useState("");
  const [deliveries, setDeliveries] = useState([]);
  const [selected, setSelected] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    api.get("/delivery").then((r) => setDeliveries(r.data)).catch(() => {});
  };
  useAutoRefresh(load, { interval: 0 });

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return deliveries.slice(0, 20);
    return deliveries.filter((d) =>
      (d.do_no || "").toLowerCase().includes(term) ||
      (d.customer || "").toLowerCase().includes(term) ||
      (d.truck_no || "").toLowerCase().includes(term)
    ).slice(0, 30);
  }, [q, deliveries]);

  const openTimeline = async (d) => {
    setSelected(d);
    setLoading(true);
    setTimeline(null);
    try {
      const r = await api.get(`/delivery/${d.id}/timeline`);
      setTimeline(r.data);
    } catch (e) {
      toast.error("Gagal memuat timeline");
    } finally { setLoading(false); }
  };

  return (
    <AppShell
      title="Timeline DO"
      subtitle="Lacak perjalanan setiap DO: Dibuat → Disetujui → Gate In → Timbang → Loading → Timbang Keluar → Gate Out"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
        {/* LEFT — search & pick DO */}
        <div className="rounded-xl bg-white border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100 bg-slate-50">
            <div className="relative">
              <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="timeline-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cari No. DO, customer, atau plat…"
                className="h-10 pl-9 font-mono text-[12px]"
              />
            </div>
            <p className="text-[10px] text-slate-500 mt-2">
              {q ? `${filtered.length} hasil` : `20 DO terbaru dari total ${deliveries.length}`}
            </p>
          </div>
          <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-8 text-center text-xs text-slate-400">Tidak ada DO ditemukan.</div>
            ) : (
              filtered.map((d) => {
                const isActive = selected?.id === d.id;
                return (
                  <button
                    key={d.id}
                    onClick={() => openTimeline(d)}
                    data-testid={`timeline-pick-${d.do_no}`}
                    className={`w-full text-left px-4 py-3 hover:bg-sky-50 transition-colors ${isActive ? "bg-sky-100 border-l-4 border-sky-500" : "border-l-4 border-transparent"}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono font-bold text-[12px] text-slate-800 truncate">{d.do_no}</span>
                      <span className="text-[9px] font-bold uppercase text-slate-500">{d.approval_status || d.status}</span>
                    </div>
                    <div className="text-[11px] text-slate-600 mt-0.5 truncate">{d.customer}</div>
                    <div className="text-[10px] text-slate-400 font-mono mt-0.5">{d.truck_no || "-"} · {d.product || "-"}</div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* RIGHT — timeline */}
        <div className="rounded-xl bg-white border border-slate-200 shadow-sm overflow-hidden">
          {!selected ? (
            <div className="py-24 text-center text-slate-400">
              <ClockClockwise size={40} weight="duotone" className="mx-auto text-slate-300 mb-3" />
              <div className="text-sm font-semibold text-slate-500">Pilih DO di sebelah kiri</div>
              <div className="text-xs text-slate-400 mt-1">Timeline lengkap akan tampil di sini.</div>
            </div>
          ) : loading || !timeline ? (
            <div className="py-24 text-center text-slate-400 text-sm">Memuat timeline…</div>
          ) : (
            <TimelineDoView data={timeline} />
          )}
        </div>
      </div>
    </AppShell>
  );
}

function TimelineDoView({ data }) {
  const { do: d, events } = data;
  const header = (
    <div className="p-5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Delivery Order</div>
          <div className="font-mono font-black text-xl text-slate-900 mt-0.5" data-testid="timeline-do-no">{d.do_no}</div>
          <div className="text-sm text-slate-700 mt-1">
            <span className="font-semibold">{d.customer}</span>
            <span className="mx-2 text-slate-300">·</span>
            <span>{d.product}</span>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px]">
          <MiniStat label="Kendaraan" value={d.truck_no || "-"} mono />
          <MiniStat label="Driver" value={d.driver || "-"} />
          <MiniStat label="Target Qty" value={`${fmtNum(d.target_qty)} L`} mono />
          <MiniStat label="Net Terkirim" value={d.net_delivered ? `${fmtNum(d.net_delivered)} kg` : "—"} mono />
        </div>
      </div>
    </div>
  );
  return <TimelineView header={header} events={events} />;
}
